// const userAgentSignature = () => {
//     if (typeof navigator === "undefined") return false;

//     const _patterns = [
//       /googlebot/i,
//       /adsbot-google/i,
//       /bingbot/i,
//       /slurp/i,
//       /duckduckbot/i,
//       /baiduspider/i,
//       /yandexbot/i,
//       /facebookexternalhit/i,
//       /twitterbot/i
//     ];

//     const ua = navigator.userAgent || "";
//     return _patterns.some((regex) => regex.test(ua));
//   };

//   export const shouldRedirectBot = () => {
//     try {
//       return userAgentSignature();
//     } catch (e) {
//       return false;
//     }
//   };
/**
 * Bot Detection Utility - Enhanced with Official Documentation (2025)
 *
 * Based on official user agent patterns from:
 * - Google Developers Documentation (developers.google.com)
 * - Meta/Facebook Developers Documentation (developers.facebook.com)
 * - Twitter/X Developers Documentation (developer.x.com)
 * - Microsoft Bing Webmaster Documentation
 * - Apple, LinkedIn, Pinterest, and other platform documentation
 *
 * Last Updated: June 2025
 * Research Source: See RESEARCH_OFFICIAL_USER_AGENTS.md
 */

// Enhanced bot detection with logging and caching
let cachedResult = null;
let cachedUserAgent = null;

const detectBot = () => {
  const userAgent = navigator.userAgent;

  // Return cached result if user agent hasn't changed
  if (cachedUserAgent === userAgent && cachedResult !== null) {
    return cachedResult;
  }

  const lowerUA = userAgent.toLowerCase();

  // Official bot patterns from documented sources (as of 2025)
  const botPatterns = [
    // === GOOGLE CRAWLERS (Official from Google Developer Docs) ===
    "googlebot",
    "googlebot-mobile",
    "googlebot-image",
    "googlebot-news",
    "googlebot-video",
    "google-extended",
    "adsbot-google",
    "google-site-verification",
    "google-pagespeed",
    "google-inspectiontool",
    "google-safety",
    "google-read-aloud",
    "google-favicon",
    "google-studio",
    "googleother",
    "googleweblight",
    "storebot-google",

    // === META/FACEBOOK CRAWLERS (Official from Meta Developer Docs) ===
    "facebookexternalhit",
    "facebookcatalog",
    "meta-externalagent",
    "meta-externalfetcher",
    "facebot",
    "facebook",
    "instagram",

    // === TWITTER/X CRAWLERS (Official from X Developer Docs) ===
    "twitterbot",
    "x-crawler",

    // === MICROSOFT/BING CRAWLERS (Official Sources) ===
    "bingbot",
    "bingpreview",
    "adidxbot",
    "microsoftpreview",
    "msnbot",

    // === APPLE CRAWLERS (Official from Apple) ===
    "applebot",
    "applenewsbot",

    // === LINKEDIN CRAWLERS (Official) ===
    "linkedinbot",

    // === PINTEREST CRAWLERS (Official) ===
    "pinterest",
    "pinterestbot",

    // === WHATSAPP ===
    "whatsapp",

    // === OTHER SOCIAL MEDIA (Documented) ===
    "redditbot",
    "telegrambot",
    "discordbot",
    "slackbot",
    "snapchat",

    // === SEARCH ENGINES (Official Documentation) ===
    "duckduckbot",
    "yandexbot",
    "baiduspider",
    "sogou",
    "exabot",

    // === SEO & ANALYTICS TOOLS (Official User Agents) ===
    "ahrefsbot",
    "semrushbot",
    "mj12bot",
    "dotbot",
    "rogerbot",
    "screaming frog",
    "sitebulb",
    "linkdexbot",
    "serpstatbot",

    // === MONITORING SERVICES (Official) ===
    "uptimerobot",
    "pingdom",
    "gtmetrix",
    "site24x7",
    "newrelic",
    "datadog",
    "statuscake",
    "montastic",

    // === SECURITY & VULNERABILITY SCANNERS ===
    "netsparker",
    "acunetix",
    "qualys",
    "rapid7",
    "nessus",
    "openvas",
    "nikto",
    "sqlmap",
    "burp",
    "zaproxy",

    // === DEVELOPMENT & TESTING TOOLS ===
    "selenium",
    "webdriver",
    "puppeteer",
    "playwright",
    "cypress",
    "chrome-lighthouse",
    "pagespeed",
    "browserstack",
    "saucelabs",

    // === API CLIENTS & SCRIPTS ===
    "python-requests",
    "python-urllib",
    "curl",
    "wget",
    "httpie",
    "postman",
    "insomnia",
    "node.js",
    "axios",
    "scrapy",

    // === CMS & E-COMMERCE PLATFORMS ===
    "wordpress",
    "drupal",
    "shopify",
    "woocommerce",
    "magento",
    "prestashop",
    "bigcommerce",
    "squarespace",

    // === AI & ML CRAWLERS (Emerging 2024-2025) ===
    "openai",
    "anthropic",
    "claude",
    "chatgpt",
    "gpt",
    "bard",
    "huggingface",
    "cohere",
    "perplexity",
    "ai2bot",

    // === ARCHIVE & RESEARCH ===
    "wayback",
    "archive.org",
    "internetarchive",
    "commoncrawl",
    "heritrix",
    "nutchwax",

    // === GENERIC BOT PATTERNS (Common Indicators) ===
    "crawler",
    "spider",
    "scraper",
    "bot/",
    "bot-",
    "_bot",
    "indexer",
    "fetcher",
    "checker",
    "validator",
    "monitor",
    "scanner",
    "probe",
    "harvest",
    "collect",

    // === URL PATTERNS (Often in Bot User Agents) ===
    "+http://",
    "+https://",
    ".com/bot",
    ".org/bot",
    "/bot.html",

    // === AUTOMATION INDICATORS ===
    "headless",
    "phantom",
    "automated",
    "script",
    "service",
    "tool",
    "utility",
    "library",
    "framework",
    "engine",
  ];

  const isBot = botPatterns.some((pattern) => lowerUA.includes(pattern));

  // Enhanced checks for common bot characteristics
  const hasNoJavaScript = typeof window === "undefined";
  const hasHeadlessChrome =
    lowerUA.includes("headlesschrome") || lowerUA.includes("headless");
  const hasPhantomJS =
    lowerUA.includes("phantomjs") || lowerUA.includes("phantom");
  const hasSelenium =
    lowerUA.includes("selenium") || lowerUA.includes("webdriver");
  const hasPuppeteer =
    lowerUA.includes("puppeteer") || lowerUA.includes("chrome-headless");
  const hasPlaywright = lowerUA.includes("playwright");

  // Check for suspicious user agent patterns
  const isVeryShort = userAgent.length < 50;
  const hasHttpInUA =
    lowerUA.includes("http://") || lowerUA.includes("https://");
  const hasEmailInUA = lowerUA.includes("@") && lowerUA.includes(".");
  const hasCommonBotFormat =
    lowerUA.includes("compatible;") &&
    !lowerUA.includes("msie") &&
    !lowerUA.includes("trident");
  const missingCommonBrowserInfo =
    !lowerUA.includes("mozilla") &&
    !lowerUA.includes("webkit") &&
    !lowerUA.includes("gecko");

  // Check for automation frameworks
  const hasAutomationFramework = [
    "cypress",
    "jest",
    "mocha",
    "jasmine",
    "karma",
    "protractor",
    "nightwatch",
    "webdriverio",
    "codecept",
    "testcafe",
  ].some((framework) => lowerUA.includes(framework));

  const finalResult =
    isBot ||
    hasNoJavaScript ||
    hasHeadlessChrome ||
    hasPhantomJS ||
    hasSelenium ||
    hasPuppeteer ||
    hasPlaywright ||
    isVeryShort ||
    hasHttpInUA ||
    hasEmailInUA ||
    hasCommonBotFormat ||
    missingCommonBrowserInfo ||
    hasAutomationFramework;

  // Cache the result
  cachedUserAgent = userAgent;
  cachedResult = finalResult;

  // Debug logging (only in development)
  if (process.env.NODE_ENV === "development") {
    console.log(
      "🔍 Official Bot Detection Analysis (Based on 2025 Documentation):",
      {
        userAgent: userAgent,
        isBot: finalResult,
        matchedOfficialPatterns: botPatterns.filter((pattern) =>
          lowerUA.includes(pattern)
        ),
        detectionReasons: {
          patternMatch: isBot,
          hasNoJavaScript,
          hasHeadlessChrome,
          hasPhantomJS,
          hasSelenium,
          hasPuppeteer,
          hasPlaywright,
          isVeryShort,
          hasHttpInUA,
          hasEmailInUA,
          hasCommonBotFormat,
          missingCommonBrowserInfo,
          hasAutomationFramework,
        },
        confidence: isBot ? "HIGH (Official Pattern)" : "MEDIUM (Heuristic)",
      }
    );
  }

  return finalResult;
};

export const shouldRedirectBot = detectBot;
